/**
 * A stay's care plan, full-screen at `care-planner.<tenant>/stays/{id}` and
 * in-frame at `/home/a/care-planner/stays/{id}`.
 *
 * adr/0023: the app opens on the SUBJECT. This screen's whole job is to hand
 * the board the stay id from the path and, when there is one, the conversation
 * from `?c=`. It reads no plan of its own — the board does that from the
 * resource, and it is complete before anyone types.
 *
 * THE CONVERSATION IS A QUERY PARAMETER, not a path segment. There may be many
 * conversations about one stay, each belonging to the nurse who opened it,
 * while the plan belongs to the stay. Putting a conversation id in the path
 * would say the plan is one conversation's, which is exactly the mistake the
 * session-first care planner made.
 */
import { useCallback } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { AgentScreen } from '@frontend/components/agentDetail/AgentScreen'
import { useSession } from '@frontend/hooks/useSessions'
import type { SessionResponse } from '@frontend/api/types_session'

// The SAME module the main app mounts in-frame. Mounting the face here rather
// than the board underneath it is what makes the two surfaces one
// implementation — a page that reached past the face could drift from what the
// nurse sees in the twin, and agentFaces.test.ts fails when one does.
import Face from '../face'

/** The query parameter that reopens one particular conversation about this stay. */
export const CONVERSATION_PARAM = 'c'

export default function CarePlannerStay() {
  const { stayId } = useParams<{ stayId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const conversationId = searchParams.get(CONVERSATION_PARAM) ?? ''
  const { data: session } = useSession(conversationId)

  // The composer minted a conversation on its first message. Naming it in the
  // URL is what makes reload and Back come back to the same thread; `replace`
  // because arriving at the board and typing into it is one step, not two.
  const onSessionCreated = useCallback(
    (created: SessionResponse) => {
      const next = new URLSearchParams(searchParams)
      next.set(CONVERSATION_PARAM, created.id)
      setSearchParams(next, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  if (!stayId) return null

  return (
    <AgentScreen
      title={t('carePlan.title', 'Care plan')}
      onBack={() => (window.history.length > 1 ? navigate(-1) : navigate('/'))}
    >
      <Face stayId={stayId} session={session} onSessionCreated={onSessionCreated} />
    </AgentScreen>
  )
}
